import { cx } from 'utils';

/**
 * Skeleton — shimmering placeholder for content that's still loading.
 *
 * Use the `variant` presets for common shapes, or pass width/height/className
 * directly. The shimmer sweeps a soft brand highlight across a glass base.
 *
 * @param {'text'|'title'|'avatar'|'button'|'rect'} [variant='text']
 */
const VARIANTS = {
  text: 'h-4 w-full rounded-md',
  title: 'h-7 w-2/3 rounded-lg',
  avatar: 'h-11 w-11 rounded-full',
  button: 'h-11 w-28 rounded-xl',
  rect: 'h-full w-full rounded-2xl',
};

export default function Skeleton({
  variant = 'text',
  width,
  height,
  className = '',
  style,
  ...props
}) {
  return (
    <span
      aria-hidden="true"
      style={{ width, height, ...style }}
      className={cx(
        'relative block overflow-hidden bg-brand-50',
        'before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer',
        'before:bg-gradient-to-r before:from-transparent before:via-white/80 before:to-transparent',
        VARIANTS[variant] || VARIANTS.text,
        className
      )}
      {...props}
    />
  );
}

/** SkeletonText — a stack of text lines; the last is shortened for realism. */
export function SkeletonText({ lines = 3, className = '' }) {
  return (
    <div className={cx('space-y-2.5', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} variant="text" className={i === lines - 1 ? 'w-1/2' : 'w-full'} />
      ))}
    </div>
  );
}

/** SkeletonCard — a ready-made white card placeholder. */
export function SkeletonCard({ className = '' }) {
  return (
    <div className={cx('rounded-2xl border border-slate-100 bg-white p-6 shadow-card', className)}>
      <div className="flex items-center gap-3">
        <Skeleton variant="avatar" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
      <SkeletonText lines={3} className="mt-5" />
    </div>
  );
}
